"use client";
import { useState } from "react";
import { useSnackbar } from "~/components/snackbar";
import type { Song } from "~/types";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";

interface SongRenameDialogProps {
  song: Song;
  onClose: () => void;
  onSave: (songId: string, title: string) => Promise<void>;
}

export default function SongRenameDialog({
  song,
  onClose,
  onSave,
}: SongRenameDialogProps) {
  const [title, setTitle] = useState(song.title);
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const handleSave = async () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSave(song.id, trimmed);
      showSuccess("Titlul a fost actualizat!");
      onClose();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Eroare la salvarea titlului",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Schimbă titlul</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Titlu"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void handleSave();
          }}
          disabled={saving}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Anulează
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || !title.trim()}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {saving ? "Se salvează..." : "Salvează"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
